import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaSun, FaMoon } from 'react-icons/fa';

const Header = ({ isDarkMode, toggleTheme }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Scroll to top when navigation is clicked
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
    scrollToTop();
  }, [location]);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      isScrolled 
        ? 'backdrop-blur-md shadow-xl' 
        : 'shadow-lg'
    } ${
      isDarkMode 
        ? isScrolled 
          ? 'bg-gray-900/90 border-b border-gray-700/50' 
          : 'bg-gray-900 border-b border-gray-700'
        : isScrolled
          ? 'bg-navy-900/90'
          : 'bg-navy-900'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 lg:h-20">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center hover:opacity-90 transition-opacity duration-300">
              {/* Short logo for mobile */}
              <img
                src="/logo/logo-short.png"
                alt="Trends Dynamiques Consulting Limited"
                className="h-14 w-auto object-contain md:hidden"
              />
              {/* Long logo for desktop */}
              <img
                src="/logo/logo-long.png"
                alt="Trends Dynamiques Consulting Limited"
                className="hidden md:block h-40 w-auto object-contain"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-2">
            {[
              { path: '/', label: 'Home' },
              { path: '/services', label: 'Services' },
              { path: '/about', label: 'About' },
              { path: '/contact', label: 'Contact' }
            ].map(({ path, label }) => (
              <Link
                key={path}
                to={path}
                className={`relative px-4 lg:px-5 py-2.5 rounded-xl font-medium transition-all duration-300 ${
                  isActive(path)
                    ? 'bg-secondary-500 text-white shadow-lg scale-105'
                    : 'text-white hover:bg-white/10 hover:scale-105'
                }`}
              >
                <span className="text-sm lg:text-base">{label}</span>
                {isActive(path) && (
                  <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-white rounded-full animate-pulse"></div>
                )}
              </Link>
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-3">
            {/* Contact Button (Desktop) */}
            

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="relative p-2.5 lg:p-3 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all duration-300 hover:scale-110 group"
              aria-label="Toggle theme"
            >
              {isDarkMode ? (
                <FaSun className="text-lg lg:text-xl group-hover:rotate-180 transition-transform duration-500" />
              ) : (
                <FaMoon className="text-lg lg:text-xl group-hover:rotate-12 transition-transform duration-300" />
              )}
            </button>
            
            {/* Mobile Menu Toggle */}
            <button
              onClick={toggleMobileMenu}
              className="md:hidden relative p-2.5 text-white focus:outline-none focus:ring-2 focus:ring-white/30 rounded-xl bg-white/10 hover:bg-white/20 transition-all duration-300"
              aria-label="Toggle menu"
            >
              <div className="w-6 h-5 relative flex flex-col justify-between">
                <span className={`w-full h-0.5 bg-white rounded-full transition-all duration-300 ${
                  isMobileMenuOpen ? 'transform rotate-45 translate-y-2' : ''
                }`}></span>
                <span className={`w-full h-0.5 bg-white rounded-full transition-all duration-300 ${
                  isMobileMenuOpen ? 'opacity-0' : ''
                }`}></span>
                <span className={`w-full h-0.5 bg-white rounded-full transition-all duration-300 ${
                  isMobileMenuOpen ? 'transform -rotate-45 -translate-y-2' : ''
                }`}></span>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className={`md:hidden transition-all duration-300 overflow-hidden ${
          isMobileMenuOpen 
            ? 'max-h-96 opacity-100 pb-4' 
            : 'max-h-0 opacity-0'
        }`}>
          <nav className="pt-4 border-t border-white/20">
            <ul className="space-y-2">
              {[
                { path: '/', label: 'Home' },
                { path: '/services', label: 'Services' },
                { path: '/about', label: 'About' },
                { path: '/contact', label: 'Contact' }
              ].map(({ path, label }) => (
                <li key={path}>
                  <Link
                    to={path}
                    className={`flex items-center px-4 py-3 rounded-xl font-medium transition-all duration-300 ${
                      isActive(path)
                        ? 'bg-secondary-500 text-white shadow-lg'
                        : 'text-white hover:bg-white/10'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span>{label}</span>
                    {isActive(path) && (
                      <div className="ml-auto w-2 h-2 bg-white rounded-full animate-pulse"></div>
                    )}
                  </Link>
                </li>
              ))}
            </ul>

          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;